import React, { useState } from "react";
import { Calendar, Briefcase, Mic2, Sparkles, CheckCircle, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import ModelApplicationForm from "../components/forms/ModelApplicationForm";
import StudioOfferForm from "../components/forms/StudioOfferForm";
import EventOfferForm from "../components/forms/EventOfferForm";
import BrandProposalForm from "../components/forms/BrandProposalForm";
import CalendlyEmbed from "../components/CalendlyEmbed";

const HERO_IMAGE = "https://customer-assets-lxgj4vgw.emergentagent.net/job_732c40bf-63ea-451c-b997-eb6b46d3eef5/artifacts/0rfj0f0z_COM.png";

const SUBMISSION_TYPES = [
  {
    id: "model",
    icon: Sparkles,
    title: "Apply as Model",
    description: "Join Gio's exclusive performer roster",
    color: "from-pink-500 to-rose-600"
  },
  {
    id: "studio",
    icon: Briefcase,
    title: "Propose Studio Shoot",
    description: "Invite Gio to collaborate at your studio",
    color: "from-purple-500 to-indigo-600"
  },
  {
    id: "event",
    icon: Mic2,
    title: "Book for Live Event",
    description: "Hire Gio for a show or appearance",
    color: "from-blue-500 to-cyan-600"
  },
  {
    id: "brand",
    icon: Calendar,
    title: "Partner as Brand",
    description: "Collaborate on sponsored content",
    color: "from-amber-500 to-yellow-600"
  }
];

export default function UnifiedApplyPage() {
  const [selectedType, setSelectedType] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submittedType, setSubmittedType] = useState("");
  const [showSchedule, setShowSchedule] = useState(false);

  const handleSubmitSuccess = (type) => {
    setSubmitted(true);
    setSubmittedType(type);
    setShowSchedule(false);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-pink-900 flex items-center justify-center p-6">
        <div className="max-w-4xl w-full">
          {!showSchedule ? (
            <div className="bg-black/40 backdrop-blur-xl rounded-2xl p-8 border border-white/10 text-center">
              <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
              <h1 className="text-3xl font-bold text-white mb-3">Submission Received!</h1>
              <p className="text-gray-300 mb-6">
                Thank you for your {submittedType} submission! Gio reviews every submission personally. You'll hear from us if there's a good fit for collaboration.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-xl mx-auto">
                <button
                  onClick={() => setShowSchedule(true)}
                  className="px-6 py-3 rounded-lg bg-gradient-to-r from-purple-500 to-blue-600 text-white font-medium hover:from-purple-600 hover:to-blue-700 transition flex items-center justify-center gap-2"
                >
                  <Calendar className="w-5 h-5" />
                  Schedule a Call
                </button>
                <button
                  onClick={() => { setSubmitted(false); setSelectedType(null); }}
                  className="px-6 py-3 rounded-lg bg-gradient-to-r from-pink-500 to-purple-600 text-white font-medium hover:from-pink-600 hover:to-purple-700 transition"
                >
                  Submit Another
                </button>
                <button
                  onClick={() => window.location.href = "/"}
                  className="px-6 py-3 rounded-lg bg-white/10 text-white font-medium hover:bg-white/20 transition"
                >
                  Return Home
                </button>
              </div>
            </div>
          ) : (
            <div>
              <button
                onClick={() => setShowSchedule(false)}
                className="mb-4 px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white font-medium transition"
              >
                ← Back to Confirmation
              </button>
              <CalendlyEmbed
                calendlyUrl={process.env.REACT_APP_CALENDLY_COLLAB_URL}
                title="Schedule Your Collaboration Meeting"
                description="Let's discuss your project in detail"
                height={750}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  if (!selectedType) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-pink-900 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Hero Section with Image */}
          <div className="text-center mb-12">
            <div className="mb-8">
              <img 
                src={HERO_IMAGE} 
                alt="giopilos.com" 
                className="w-full max-w-4xl mx-auto h-auto rounded-2xl shadow-2xl mb-8 bg-transparent"
                style={{ maxHeight: "300px", objectFit: "cover", background: "transparent" }}
              />
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-4">
                <span className="bg-gradient-to-r from-pink-400 via-purple-400 to-blue-400 bg-clip-text text-transparent">
                  Work With Gio
                </span>
              </h1>
              <p className="text-xl sm:text-2xl text-gray-300 max-w-3xl mx-auto">
                Choose your collaboration type below to get started
              </p>
            </div>
          </div>

          {/* Submission Type Selector */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {SUBMISSION_TYPES.map((type) => {
              const Icon = type.icon;
              return (
                <button
                  key={type.id}
                  onClick={() => setSelectedType(type.id)}
                  className="group relative overflow-hidden rounded-2xl bg-black/40 backdrop-blur-xl border border-white/10 hover:border-white/30 transition-all duration-300 p-8 text-left hover:scale-105 hover:shadow-2xl"
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${type.color} opacity-0 group-hover:opacity-10 transition-opacity`} />
                  <Icon className={`w-12 h-12 mb-4 text-transparent bg-gradient-to-br ${type.color} bg-clip-text`} />
                  <h3 className="text-xl font-bold text-white mb-2">{type.title}</h3>
                  <p className="text-gray-400 text-sm">{type.description}</p>
                  <div className="mt-4 flex items-center text-sm font-medium">
                    <span className={`text-transparent bg-gradient-to-r ${type.color} bg-clip-text`}>
                      Get Started →
                    </span>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Info Section */}
          <div className="mt-16 text-center">
            <div className="inline-block bg-purple-900/20 rounded-xl p-6 border border-purple-500/30">
              <p className="text-gray-300 text-sm">
                🔒 All submissions are reviewed personally by Gio. Your information is kept strictly confidential.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render selected form
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-pink-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => setSelectedType(null)}
          className="mb-6 px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white font-medium transition flex items-center gap-2"
        >
          ← Back to Selection
        </button>

        {/* Form Container */}
        {selectedType === "model" && <ModelApplicationForm onSuccess={() => handleSubmitSuccess("Model Application")} />}
        {selectedType === "studio" && <StudioOfferForm onSuccess={() => handleSubmitSuccess("Studio Shooting Offer")} />}
        {selectedType === "event" && <EventOfferForm onSuccess={() => handleSubmitSuccess("Live Show Offer")} />}
        {selectedType === "brand" && <BrandProposalForm onSuccess={() => handleSubmitSuccess("Brand Proposal")} />}
      </div>
    </div>
  );
}